import { useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  AUDIENCE_VISIO_CONSENT_VERSION,
  getApiErrorDetail,
  postAudienceVisioConsent,
} from "@/lib/api";

const MARINE_DEEP = "oklch(0.115 0.038 243)";
const GOLD = "oklch(0.695 0.105 68)";

interface JitsiConsentDialogProps {
  open: boolean;
  caseId: number;
  onOpenChange: (open: boolean) => void;
  onConsented: () => void;
}

// Modale "Preuve forte" pour AUDIENCE_VISIO. Materialisation TSA Zetes
// + ancrage Merkle cote core. Une fois confirme, le consentement
// survit plusieurs annees -- texte volontairement explicite pour qu'il
// se relise sans contexte si on le ressort plus tard.
export function JitsiConsentDialog({
  open,
  caseId,
  onOpenChange,
  onConsented,
}: JitsiConsentDialogProps) {
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setAccepted(false);
    setSubmitting(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const onSubmit = async () => {
    if (!accepted || submitting) return;
    setSubmitting(true);
    try {
      await postAudienceVisioConsent(caseId);
      toast.success(
        "Consentement enregistre. Materialisation probatoire (TSA + Merkle) effectuee.",
      );
      reset();
      onConsented();
    } catch (error) {
      toast.error(
        getApiErrorDetail(error) ??
          "Echec de la materialisation probatoire. Reessayez dans un instant.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-lg p-0 gap-0 border-0"
        style={{
          background: MARINE_DEEP,
          border: `1px solid color-mix(in oklch, ${GOLD} 25%, transparent)`,
          boxShadow: `0 20px 60px color-mix(in oklch, ${GOLD} 12%, transparent)`,
          color: "oklch(1 0 0)",
        }}
      >
        <DialogHeader className="px-8 pt-8 pb-3 space-y-3">
          <div
            className="w-12 h-12 flex items-center justify-center rounded-sm"
            style={{
              background: `color-mix(in oklch, ${GOLD} 15%, transparent)`,
              border: `1px solid color-mix(in oklch, ${GOLD} 40%, transparent)`,
            }}
          >
            <ShieldCheck size={20} style={{ color: GOLD }} />
          </div>
          <DialogTitle
            className="text-2xl text-left"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: "oklch(1 0 0)",
              letterSpacing: "0.01em",
            }}
          >
            Consentement a l'audience par visioconference
          </DialogTitle>
          <DialogDescription
            className="text-left text-sm"
            style={{
              color: "oklch(0.85 0.01 243)",
              fontFamily: "'Inter', sans-serif",
              lineHeight: 1.55,
            }}
          >
            Preuve forte (TSA qualifie eIDAS via Zetes + ancrage Merkle).
            Une fois materialise, ce consentement ne peut plus etre
            revoque unilateralement et fait foi pour toute la procedure
            arbitrale (dossier #{caseId}).
          </DialogDescription>
        </DialogHeader>

        <div
          className="px-8 py-4 text-sm"
          style={{
            color: "oklch(0.92 0.01 243)",
            fontFamily: "'Inter', sans-serif",
            lineHeight: 1.6,
          }}
        >
          <p className="mb-3">En cochant la case ci-dessous, vous declarez :</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>
              accepter expressement la tenue de l'audience par
              visioconference securisee (Jitsi), au lieu d'une audience
              physique ;
            </li>
            <li>
              avoir ete informe que la session est journalisee de maniere
              append-only (conservation 10 ans) sans enregistrement video ;
            </li>
            <li>
              reconnaitre que ce consentement est ancre cryptographiquement
              et ne peut etre conteste qu'au titre des exceptions d'ordre
              public.
            </li>
          </ul>

          <label className="mt-5 flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={accepted}
              onCheckedChange={(v) => setAccepted(v === true)}
              disabled={submitting}
              className="mt-0.5"
            />
            <span>
              J'accepte la tenue de l'audience par visioconference et je
              consens a la materialisation probatoire de cet accord
              (version{" "}
              <code style={{ color: GOLD }}>
                {AUDIENCE_VISIO_CONSENT_VERSION}
              </code>
              ).
            </span>
          </label>
        </div>

        <DialogFooter className="px-8 py-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
          >
            Annuler
          </Button>
          <Button
            type="button"
            onClick={onSubmit}
            disabled={!accepted || submitting}
            style={{
              background: GOLD,
              color: MARINE_DEEP,
              opacity: !accepted || submitting ? 0.6 : 1,
            }}
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="animate-spin mr-2" />
                Materialisation...
              </>
            ) : (
              "Confirmer et signer"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
